import React from "react";
import {
  Sparkles,
  Instagram,
  Facebook,
  Layout,
  Smartphone,
} from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";

interface SocialVariationsModalProps {
  open: boolean;
  onClose: () => void;
  variations: Record<string, { public_url: string }>;
  originalUrl: string;
  post?: any;
  onDuplicate?: (platform: string, variationKey: string) => void;
}

const SocialVariationsModal: React.FC<SocialVariationsModalProps> = ({
  open,
  onClose,
  variations,
  originalUrl,
  onDuplicate,
}) => {
  const { t } = useTranslation();
  const formats = [
    {
      key: "square",
      name: "Instagram / FB Feed",
      ratio: "1:1",
      icon: <Instagram className="w-3 h-3" />,
      platform: "instagram",
    },
    {
      key: "portrait",
      name: "Instagram Portrait",
      ratio: "4:5",
      icon: <Instagram className="w-3 h-3" />,
      platform: "instagram",
    },
    {
      key: "stories",
      name: "Stories / Reels",
      ratio: "9:16",
      icon: <Smartphone className="w-3 h-3" />,
      platform: "instagram",
    },
    {
      key: "landscape",
      name: "Facebook / Google",
      ratio: "1.91:1",
      icon: <Facebook className="w-3 h-3" />,
      platform: "facebook",
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnOverlayClick
      title={t("Otimização Social Automática")}
      subtitle={t(
        "A IA redimensionou sua imagem para os formatos ideais de cada rede",
      )}
      icon={<Sparkles className="h-5 w-5 text-purple-500" />}
      maxWidth="max-w-5xl"
    >
      <div className="space-y-8 p-1">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {formats.map((format) => {
            const varData = variations[format.key];
            return (
              <div key={format.key} className="space-y-3 group">
                <div className="flex items-center justify-between px-1">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-50 rounded-lg text-purple-600">
                      {format.icon}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-gray-700">
                      {format.name}
                    </span>
                  </div>
                  <span className="text-[10px] font-bold text-purple-500 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">
                    {format.ratio}
                  </span>
                </div>

                <div
                  className={cn(
                    "relative bg-gray-900 rounded-2xl overflow-hidden border-2 border-transparent group-hover:border-purple-400 transition-all shadow-xs group-hover:shadow-xl group-hover:-translate-y-1 duration-300",
                    format.key === "square"
                      ? "aspect-square"
                      : format.key === "portrait"
                        ? "aspect-4/5"
                        : format.key === "stories"
                          ? "aspect-9/16"
                          : "aspect-[1.91/1]",
                  )}
                >
                  {varData ? (
                    <img
                      src={varData.public_url}
                      alt={format.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center gap-2">
                      <Layout className="w-8 h-8 text-gray-300" />
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">
                        {t("Formato não disponível")}
                      </p>
                    </div>
                  )}

                  {varData && (
                    <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-4 gap-3">
                      <p className="text-[10px] text-white/80 font-medium leading-tight">
                        {t(
                          "Clique abaixo para agendar este post especificamente nesta rede.",
                        )}
                      </p>
                      <Button
                        size="sm"
                        variant="primary"
                        className="w-full py-2 shadow-lg"
                        onClick={() => {
                          if (onDuplicate) {
                            onDuplicate(format.platform, format.key);
                          }
                        }}
                      >
                        {t("Agendar nesta rede")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="pt-6 border-t border-purple-100">
          <div className="bg-linear-to-r from-purple-50 to-indigo-50 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 border border-purple-100">
            <div className="w-32 h-32 shrink-0 rounded-xl overflow-hidden border-2 border-white shadow-lg rotate-3">
              <img
                src={originalUrl}
                alt={t("Original")}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 text-center md:text-left space-y-2">
              <h4 className="text-sm font-black text-gray-800 uppercase tracking-tight">
                {t("Imagem Original Preservada")}
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed max-w-lg font-medium">
                {t(
                  "O Virtus adaptou sua imagem automaticamente. Você pode clicar em",
                )}{" "}
                <strong>"{t("Agendar nesta rede")}"</strong>{" "}
                {t(
                  "em qualquer formato acima para criar uma duplicata do post já otimizada para essa plataforma.",
                )}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t("Fechar")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onClose}
                className="shadow-lg shadow-purple-500/20"
              >
                {t("Confirmar Formatos")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SocialVariationsModal;
